import dayjs from 'utils/dayjsConfig';

const DATE_FORMATS = ['YYYY-MM-DD HH:mm:ss.SSSSSS', 'YYYY-MM-DD HH:mm:ss.SSS', 'YYYY-MM-DD HH:mm:ss'];

/**
 * Converts the serialized procurement date returned by the API (or a raw string) into a Dayjs instance.
 *
 * Attempts strict parsing against known backend formats, gracefully handling timezone-specific variants
 * and ISO-8601 strings. Returns `null` when parsing fails.
 *
 * @param {{ date: string, timezone?: string, timezone_type?: number } | string | null | undefined} value Raw PO date.
 * @returns {dayjs.Dayjs | null} Parsed Dayjs instance or `null` when parsing fails.
 */
export const toMoment = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (dayjs.isDayjs(value)) {
        return value.isValid() ? value.clone() : null;
    }

    const raw = typeof value === 'string' ? value : value?.date;
    if (!raw || typeof raw !== 'string') return null;

    const timezone = typeof value === 'object' && value ? value.timezone : undefined;
    const normalized = raw.replace(' ', 'T');

    if (timezone && typeof dayjs.tz === 'function') {
        // Try parsing with custom formats first, then converting to timezone
        for (const format of DATE_FORMATS) {
            const parsedDayjs = dayjs(raw, format, true);
            if (parsedDayjs.isValid()) {
                const zonedDayjs = parsedDayjs.tz(timezone);
                if (zonedDayjs.isValid()) return zonedDayjs;
            }
        }

        // Try ISO-8601 format (dayjs can parse ISO strings by default)
        const parsedIsoDayjs = dayjs(normalized);
        if (parsedIsoDayjs.isValid()) {
            const zonedIsoDayjs = parsedIsoDayjs.tz(timezone);
            if (zonedIsoDayjs.isValid()) return zonedIsoDayjs;
        }
    }

    // Try parsing with custom formats (strict mode)
    for (const format of DATE_FORMATS) {
        const strictDayjs = dayjs(raw, format, true);
        if (strictDayjs.isValid()) return strictDayjs;
    }

    // Try ISO-8601 format (dayjs can parse ISO strings by default)
    const normalizedDayjs = dayjs(normalized);
    if (normalizedDayjs.isValid()) return normalizedDayjs;

    // Try UTC format
    const utcDayjs = dayjs(`${normalized}Z`);
    return utcDayjs.isValid() ? utcDayjs : null;
};

/**
 * Converts a PO date object into a valid `Date` instance.
 *
 * @param {{
 *     date?: string,
 *     timezone?: string,
 *     timezone_type?: number,
 * }} value Purchase order date value, typically a serialized object from the backend.
 * @returns {Date | null} Parsed `Date` instance or `null` when parsing fails.
 */
export const parsePurchaseOrderDate = (value) => {
    const parsedDayjs = toMoment(value);
    return parsedDayjs ? parsedDayjs.toDate() : null;
};

/**
 * Formats a date-like value into a locale-aware date string (month/day/year).
 *
 * @param {{ date?: string, timezone?: string, timezone_type?: number } | string | null | undefined} value Date to format.
 * @param {string} [fallbackText='—'] Text returned when formatting fails.
 * @returns {string} Formatted date string or fallback text when formatting fails.
 */
export const formatPurchaseOrderDate = (value, fallbackText = '—') => {
    const parsedDayjs = toMoment(value);
    if (!parsedDayjs) return fallbackText;
    return parsedDayjs.format('MM/DD/YYYY');
};

/**
 * Formats a date-like value into a locale-aware date and time string.
 *
+ * @param {{ date?: string, timezone?: string, timezone_type?: number } | string | null | undefined} value Date-time to format.
 * @param {string} [fallbackText='—'] Text returned when formatting fails.
 * @returns {string} Formatted date-time string or fallback text when formatting fails.
 */
export const formatPurchaseOrderDateTime = (value, fallbackText = '—') => {
    const parsedDayjs = toMoment(value);
    if (!parsedDayjs) return fallbackText;
    return parsedDayjs.format('MM/DD/YYYY HH:mm:ss');
};

/**
 * Formats a PO date value into an ISO date string suitable for `type="date"` inputs.
 *
 * @param {{ date?: string, timezone?: string, timezone_type?: number } | string | null | undefined} value Date to format.
 * @returns {string} Formatted YYYY-MM-DD string or empty string when parsing fails.
 */
export const formatPurchaseOrderDateForInput = (value) => {
    const parsedDayjs = toMoment(value);
    if (!parsedDayjs) return '';
    return parsedDayjs.format('YYYY-MM-DD');
};

/**
 * Formats a PO date value into an ISO date-time string suitable for `type="datetime-local"` inputs.
 *
 * @param {{ date?: string, timezone?: string, timezone_type?: number } | string | null | undefined} value Date-time to format.
 * @returns {string} Formatted YYYY-MM-DDTHH:mm string or empty string when parsing fails.
 */
export const formatPurchaseOrderDateTimeForInput = (value) => {
    const parsedDayjs = toMoment(value);
    if (!parsedDayjs) return '';
    return parsedDayjs.format('YYYY-MM-DD[T]HH:mm');
};

/**
 * Determines whether a date-like value resolves to a midnight timestamp.
 *
 * @param {{ date?: string, timezone?: string, timezone_type?: number } | string | null | undefined} value Date-time candidate.
 * @returns {boolean} `true` when the parsed date exists and time is 00:00; otherwise false.
 */
export const isMidnightPurchaseOrderDateTime = (value) => {
    const parsedDayjs = toMoment(value);
    if (!parsedDayjs) return false;
    return (
        parsedDayjs.hour() === 0 &&
        parsedDayjs.minute() === 0 &&
        parsedDayjs.second() === 0 &&
        parsedDayjs.millisecond() === 0
    );
};

/**
 * Finds the week and year for a scheduled delivery date.
 * Custom PO logic:
 * If scheduled_delivery_date is Thursday - Sunday, week = scheduled_delivery_date week+1
 * If scheduled_delivery_date is Monday - Wednesday, week = scheduled_delivery_date
 * If scheduled_delivery_date is in week 53, year = scheduled_delivery_date year+1
 *
 * @param {string} date Scheduled delivery date.
 * @returns {Object} Week and year.
 * @returns {number} Week.
 * @returns {number} Year.
 */
export const findWeekAndYearForScheduledDeliveryDate = (date) => {
    const parsedDayjs = toMoment(date);
    if (!parsedDayjs) return null;
    const week = parsedDayjs.isoWeek();
    const year = parsedDayjs.isoWeekYear();
    const weeksInYear = parsedDayjs.isoWeeksInYear();
    const isoWeekday = parsedDayjs.isoWeekday(); // 1=Mon, 7=Sun
    if (isoWeekday >= 4) {
        const incrementedWeek = week + 1;
        if (incrementedWeek > weeksInYear) {
            return {
                week: 1,
                year: year + 1,
            };
        }
        return {
            week: incrementedWeek,
            year,
        };
    }
    return {
        week,
        year,
    };
};
